import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import type { ModalContextType } from "@/components/core/modals/types";
import { useModal } from "@/components/core/modals/use-modal.ts";
import Tooltip from "@/components/core/tooltip";
import { FileViewer } from "@/components/document-viewer/file-viewer.tsx";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import type { Media } from "@/features/dashboard/types/media.ts";
import { downloadFile } from "@/utils/download-file";
import { formatDate } from "@/utils/format-date.ts";
import { formatNumber } from "@/utils/format-number.ts";
import { IconDatabaseExclamation, IconDownload, IconEye, IconFileTypePdf, IconPlus, IconPrinter, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import CaseFileUploadModal from "../case-classification/case-file-upload-modal";
import { useCaseRecordMediaDeleteMutation, useCaseRecordPaymentProofMutation } from "../case-record/api/queries/case-records-query";
import type { CaseRecord } from "../case-record/types/case-records";

interface FileUploadButtonProps {
	modal: ModalContextType;
	data: CaseRecord;
	setFiles: React.Dispatch<React.SetStateAction<File[]>>;
	files: File[];
}

function FileUploadButton({ modal, data }: FileUploadButtonProps) {
	const [files, setFiles] = useState<File[]>([]);
	const attachmentsMutation = useCaseRecordPaymentProofMutation();

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(event.target.files || []);
		if (selectedFiles.length > 0) {
			const updatedFiles = [...files, ...selectedFiles];
			setFiles(updatedFiles);

			const mediaModal = modal.openModal({
				title: "Upload Case Complaint Documents",
				size: "lg",
				showHeader: true,
				showFooter: false,
				component: (
					<CaseFileUploadModal
						onSubmit={async (value) => {
							await attachmentsMutation.mutateAsync({
								id: data.id,
								files: value,
							});
							setFiles([]);
							modal.closeModal(mediaModal);
						}}
						initialFiles={updatedFiles}
					/>
				),
			});
		}
	};

	return (
		<Button className="relative" type="button" size="sm">
			<input type="file" multiple className="absolute opacity-0 inset-0" onChange={handleFileChange} />
			<IconPlus size={14} stroke={2} />
			<span className="">Add File</span>
		</Button>
	);
}

export default function CaseAttachments({
	title = "media Receipts",
	data,
	allowRemove = true,
	allowPreview = true,
	allowPrint = false,
	allowDownload = false,
}: {
	title: string;
	data: CaseRecord;
	counts: number;
	allowRemove: boolean;
	allowPreview: boolean;
	allowPrint: boolean;
	allowDownload: boolean;
}) {
	const modal = useModal();
	const documentTokenMutation = useDocumentConfigMutation();
	const attachmentDeleteMutation = useCaseRecordMediaDeleteMutation();

	const handleDownload = (attachment: Media) => {
		downloadFile(attachment.original_url, attachment.file_name);
	};

	const handleClick = async (attachment: Media) => {
		const fileExtension = attachment.file_name.split(".").pop();

		let response = null;

		if (!["image/jpeg", "image/png", "png", "jpeg", "video/mp4", "mp4"].includes(fileExtension)) {
			response = await documentTokenMutation.mutateAsync({
				file_type: fileExtension,
				title: attachment.name,
				url: attachment.original_url,
				mode: "view",
			});
		}

		modal.openModal({
			title: "Default Modal",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} token={response ? response?.token : null} options={response ? response?.options : null} />,
		});
	};
	const [files, setFiles] = useState<File[]>([]);

	const handleRemove = async (caseRecordId: string, mediaId: string) => {
		const m = modal.openModal({
			type: "confirm",
			title: "Confirm Archive",
			variant: "red",
			description: "Are you sure you want to remove this media?",
			onConfirm: async () => {
				await attachmentDeleteMutation.mutateAsync({ caseRecordId, mediaId });
				modal.closeModal(m);
			},
		});
	};

	return (
		<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
			<div className="flex flex-row items-center justify-between w-full p-4 gap-4 bg-slate-50">
				<span className="text-base font-semibold">{title}</span>
				<FileUploadButton modal={modal} data={data} files={files} setFiles={setFiles} />
			</div>

			{data?.medias?.length !== 0 ? (
				<div className="flex flex-col w-full gap-2 pb-4">
					<div className="flex flex-col w-full px-4">
						<span className="font-bold text-sm text-foreground-dark">Uploaded ({data?.medias?.length}) file/s</span>
						<span className="relative inline-flex text-sm text-foreground/70">
							Review the uploaded files carefully before confirming. Drag 'n' drop files on the drop zone above to add more files.
						</span>
					</div>

					{data?.medias?.map((media: Media, index) => (
						<div key={index} className="flex flex-col w-full px-4 py-1 gap-2">
							<div className="flex flex-col w-full gap-2">
								<div className="flex flex-row w-full gap-2">
									<div className="inline-flex justify-center items-center w-[50px] h-[50px] rounded-md bg-red-500 text-white">
										<IconFileTypePdf size={24} stroke={1.5} />
									</div>
									<div className="inline-flex flex-col w-auto">
										<button type="button" onClick={() => handleClick(media)} className="text-left text-base font-semibold text-blue-600 hover:text-blue-500">
											{media.file_name}
										</button>
										<span className="text-sm text-foreground/70">Uploaded on {formatDate(media.created_at)}</span>
										<span className="text-sm text-foreground/70">{formatNumber(media.size, "0,0b")}</span>
									</div>
									<div className="inline-flex w-auto gap-3 ml-auto">
										<div className="inline-flex flex-row w-auto gap-1">
											{allowDownload && (
												<Tooltip content="Download" side="top">
													<Button size="icon" variant="ghost" onClick={() => handleDownload(media)}>
														<IconDownload size={16} stroke={2} />
													</Button>
												</Tooltip>
											)}
											{allowPreview && (
												<Tooltip content="Preview" side="top">
													<Button size="icon" variant="ghost" onClick={() => handleClick(media)}>
														<IconEye size={16} stroke={2} />
													</Button>
												</Tooltip>
											)}
											{allowPrint && (
												<Tooltip content="Print" side="top">
													<Button size="icon" variant="ghost">
														<IconPrinter size={16} stroke={2} />
													</Button>
												</Tooltip>
											)}
											{allowRemove && data.status === "pending" && (
												<Tooltip content="Remove" side="top">
													<Button size="icon" variant="danger" type="button" onClick={() => handleRemove(data.id!, media.id)}>
														<IconTrash size={16} stroke={2} />
													</Button>
												</Tooltip>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			) : (
				<Blanket width="sm">
					<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
					<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
				</Blanket>
			)}
		</div>
	);
}
